import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { compose, debounce } from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Modal from '../../../commonComponents/modal/Modal';
import Notifications from '../../../services/notifications';
import MetaContext from '../../../contextProviders/MetaContext';
import QRCodeComponent from '../../../commonComponents/widgets/QRCodeComponent';
import ReactLoader from '../../../commonComponents/loader/ReactLoader';
import ConfigsContext from '../../../contextProviders/ConfigsContext';

/**
 * User profile main page
 */
function ShareModal({
  t,
  _csrf,
  url: urlParam,
  onClose,
  isShareUserPage,
  marketProductId,
  userPageName,
  isMarketPlaceEnabled,
}) {
  const { configs } = useContext(MetaContext);
  const { tenant } = useContext(ConfigsContext);
  const inputRef = useRef(null);
  const customShareLinkRef = useRef(null);

  const [url, setUrl] = useState(urlParam);
  const [customShareLink, setCustomShareLink] = useState(null);
  const [updatedCustomSharedUrl, setUpdatedCustomUrl] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const fetchMarketProductLink = async productId => {
    const endpoint = `/api/market/product/${productId}/share/link`;

    const { data: response } = await axios.post(
      endpoint,
      {},
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      },
    );

    return response.data;
  };

  useEffect(() => {
    if (marketProductId) {
      setIsLoading(true);
      fetchMarketProductLink(marketProductId)
        .then(data => {
          setUrl(data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err);
        })
        .finally(() => setIsLoading(false));
    }
  }, [marketProductId]);

  const showSuccess = () =>
    Notifications.showNotificationSuccess(
      t('success'),
      t('share_modal_copySuccess'),
    );
  const showError = () =>
    Notifications.showNotificationError(t('error'), t('share_modal_copyError'));

  const fallbackCopy = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      const result = document.execCommand('copy');
      if (result) {
        showSuccess();
      } else {
        showError();
      }
    }
  };

  const handleCopy = copyLink => {
    if (!navigator.clipboard) {
      fallbackCopy();
      return;
    }

    navigator.clipboard.writeText(copyLink).then(showSuccess).catch(showError);
  };

  const twitterShareUrl =
    'https://twitter.com/intent/tweet?' +
    `text=${
      isShareUserPage
        ? t('share.modal.userPageName.text')
        : t('certificate_share_text')
    }` +
    `&url=${encodeURIComponent(url)}`;

  const facebookAppId =
    configs && configs.facebookAppId ? configs.facebookAppId : null;
  const facebookShareUrl =
    'https://www.facebook.com/dialog/share?' +
    `app_id=${facebookAppId}` +
    '&display=page' +
    `&href=${encodeURIComponent(url)}` +
    `&redirect_url=${encodeURIComponent(
      typeof window !== 'undefined' ? window.location.href : '',
    )}`;

  const linkedInShareUrl =
    'https://www.linkedin.com/sharing/share-offsite?' +
    `url=${encodeURIComponent(url)}`;

  const mailShareUrl =
    'mailto:?' +
    `body=${
      isShareUserPage
        ? t('share.modal.userPageName.text')
        : t('certificate_share_text')
    }: ${url}` +
    `&subject=${
      isShareUserPage
        ? t('share.modal.userPageName.title')
        : t('certificate_share_title')
    }`;

  // region handle custom sponsor link
  const processUrl = useCallback(
    urlToProcess => {
      // Validate and convert to a proper URL
      let validUrl;
      try {
        validUrl = new URL(urlToProcess);
      } catch (_) {
        // If the input is not a valid URL, prepend "http://"
        try {
          validUrl = new URL(`https://${urlToProcess}`);
        } catch (__) {
          // Invalid URL even after prepending, ignore
          setUpdatedCustomUrl('');
          return;
        }
      }

      // Add query parameter
      validUrl.searchParams.set('sponsoredBy', userPageName);
      setUpdatedCustomUrl(validUrl.toString());
    },
    [userPageName],
  );

  const debouncedProcessUrl = useCallback(debounce(processUrl, 300), [
    processUrl,
  ]);

  const handleUrlChange = useCallback(
    e => {
      const urlLink = e.target.value;
      setCustomShareLink(urlLink);
      debouncedProcessUrl(urlLink);
    },
    [debouncedProcessUrl, setCustomShareLink],
  );
  // endregion handle custom sponsor link

  useEffect(() => {
    const defaultLink = `${
      tenant?.configs?.marketPlace || tenant?.configs?.userPortal || null
    }shop`;

    if (defaultLink) {
      setCustomShareLink(defaultLink);
      debouncedProcessUrl(defaultLink);
    }
  }, [tenant, debouncedProcessUrl]);
  return (
    <Modal onClose={onClose} id="share-modal">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <ReactLoader isLoading={isLoading}>
          <div className="modal-content shared-modal">
            <div className="modal-body">
              <div className="container-fluid">
                <h3 className="congratulations">{t('share_modal_title')}</h3>
                {facebookAppId && (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={facebookShareUrl}
                    className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                  >
                    <i className="socicon-facebook" />
                  </a>
                )}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={linkedInShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-linkedin" />
                </a>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={twitterShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-twitter" />
                </a>
                <a
                  href={mailShareUrl}
                  className="btn btn-metal m-btn m-btn--icon btn-lg m-btn--icon-only m-btn--pill"
                >
                  <i className="socicon-mail" />
                </a>
                <br />
                <div
                  className="form-group m-form__group"
                  style={{ marginTop: '2rem' }}
                >
                  <label htmlFor="shareLink">{t('share_modal_copyLink')}</label>
                  <div className="input-group">
                    <input
                      ref={inputRef}
                      id="shareLink"
                      type="text"
                      className="form-control m-input"
                      disabled
                      value={url}
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleCopy(url)}
                      >
                        <i className="la la-clipboard" />
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    {(isShareUserPage || marketProductId) &&
                      url?.length > 0 && (
                        <QRCodeComponent
                          value={url}
                          size={155}
                          classContainer="qr-container"
                          qrClass="qr-wrapper"
                        />
                      )}
                  </div>
                </div>
                {/** region Custom sponsor link */}
                {userPageName && isMarketPlaceEnabled ? (
                  <div
                    className="form-group m-form__group"
                    style={{ marginTop: '1rem' }}
                  >
                    <label htmlFor="shareLink">
                      {t('share.link.custom.links')}
                    </label>
                    <div className="input-group">
                      <input
                        id="customShareLink"
                        type="text"
                        onChange={handleUrlChange}
                        className="form-control m-input"
                        value={customShareLink}
                      />
                    </div>
                    <div className="input-group" style={{ marginTop: '10px' }}>
                      <input
                        ref={customShareLinkRef}
                        id="customLinkToShare"
                        type="text"
                        className="form-control m-input"
                        disabled
                        value={updatedCustomSharedUrl}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => handleCopy(updatedCustomSharedUrl)}
                        >
                          <i className="la la-clipboard" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                {/** endregion Custom sponsor link */}
              </div>
            </div>
            <div className="row shared-modal-footer-wrapper">
              <div className="col-lg-8 col-12" />
              <div className="col-lg-4 col-12 share-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => onClose(false)}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </ReactLoader>
      </div>
    </Modal>
  );
}

ShareModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isShareUserPage: PropTypes.bool,
  marketProductId: PropTypes.string,
  userPageName: PropTypes.string,
  isMarketPlaceEnabled: PropTypes.bool,
};

ShareModal.defaultProps = {
  isShareUserPage: false,
  marketProductId: null,
  userPageName: null,
  isMarketPlaceEnabled: false,
};

export default compose(withCSRF, translate)(ShareModal);
